import React from 'react';
import Dropdown from './Dropdown';
import { CONTROL_BOX } from '../../constants/inputs';

/**
 * THE MULTI-CHOICE dropdown checklist — a box-style trigger that SUMMARISES the
 * picks ("Main Unit, Hydraulics" / "Main Unit, Hydraulics +2 more"), opening a
 * list of checkboxes, each with an optional count. Built 2026-10-25 for the
 * Timesheets "Apply to" (owner, of the chip row it replaced: *"Seems clunky.
 * make it nicers"*).
 *
 * 🔑 EXTENDS `ui/Dropdown` (trigger, panel, outside-click / Escape close, focus
 * return, the phone bottom sheet) and duplicates none of it. It is NOT
 * `ui/FilterChipMenu`, which is single-choice by contract (an active option
 * becomes a pill with a ×); a multi-pick there would change every caller.
 *
 * Controlled and ignorant of its domain. The caller passes:
 *  - `sections` `[{ key, label?, items: [{ id, label, count? }] }]` — one or
 *    more lists, in display order; a section with no `label` has no heading;
 *  - `selected` (ids) and `onChange(nextIds)`;
 *  - `placeholder` — the trigger text with nothing picked;
 *  - `countNoun(n)` — the words beside a count ("3 people"), default the bare number;
 *  - `maxNamed` — how many names the trigger spells out before "+N more" (2);
 *  - `ariaLabel`, `testId` (trigger; each row `${testId}-option-<id>`).
 * The trigger is `CONTROL_BOX` — the same box as the search field beside it.
 * ♿ A row is a real `<input type="checkbox">` inside its `<label>`, so the
 * state is announced and colour carries nothing alone.
 */
export function checklistSummary(names, maxNamed = 2) {
  if (names.length === 0) return null;
  const shown = names.slice(0, maxNamed).join(', ');
  return names.length > maxNamed ? `${shown} +${names.length - maxNamed} more` : shown;
}

export default function ChecklistDropdown({
  sections,
  selected,
  onChange,
  placeholder = 'Choose…',
  countNoun = (n) => String(n),
  maxNamed = 2,
  ariaLabel = 'Choose',
  testId,
  title,
  // Opt-in (default off — every caller byte-identical): the trigger fills its row (a phone sheet).
  fullWidth = false,
}) {
  const items = sections.flatMap((s) => s.items);
  const picked = new Set(selected.map(String));
  const names = items.filter((it) => picked.has(String(it.id))).map((it) => it.label);
  const summary = checklistSummary(names, maxNamed);
  const toggle = (id) => {
    const key = String(id);
    const next = picked.has(key)
      ? selected.filter((x) => String(x) !== key)
      : [...selected, id];
    onChange(next);
  };
  const all = () => onChange(items.map((it) => it.id));
  const none = () => onChange([]);

  return (
    <Dropdown
      align="left"
      haspopup="dialog"
      ariaLabel={ariaLabel}
      title={title || summary || placeholder}
      triggerTestId={testId}
      panelClassName="w-72 p-1"
      triggerClassName={`${fullWidth ? 'flex w-full justify-between' : 'inline-flex max-w-[20rem]'} items-center gap-2 px-3 text-left ${CONTROL_BOX} hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
      label={(
        <span className={`min-w-0 truncate ${summary ? '' : 'text-gray-500 dark:text-gray-400'}`}>
          {summary || placeholder}
        </span>
      )}
    >
      <div className="max-h-80 overflow-y-auto">
        {sections.map((s) => (
          <div key={s.key} className="py-1" role="group" aria-label={s.label || ariaLabel}>
            {s.label && (
              <div className="px-2 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">{s.label}</div>
            )}
            {s.items.map((it) => {
              const on = picked.has(String(it.id));
              return (
                <label
                  key={it.id}
                  data-testid={testId ? `${testId}-option-${it.id}` : undefined}
                  className="flex min-h-[44px] md:min-h-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 dark:border-gray-600" checked={on} onChange={() => toggle(it.id)} />
                  <span className="min-w-0 flex-1 truncate">{it.label}</span>
                  {it.count != null && (
                    <span className="shrink-0 tabular-nums text-xs text-gray-500 dark:text-gray-400">{countNoun(it.count)}</span>
                  )}
                </label>
              );
            })}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <div className="flex justify-between border-t border-gray-200 px-2 pt-1 dark:border-gray-700">
          <button type="button" onClick={all} className="min-h-[44px] md:min-h-0 py-1 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300">Select all</button>
          <button type="button" onClick={none} className="min-h-[44px] md:min-h-0 py-1 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300">Clear</button>
        </div>
      )}
    </Dropdown>
  );
}
